import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Package } from 'lucide-react';

interface QuantityDistributionDisplayProps {
  specifications: any[];
  deliveryLocations: any[];
  matrix: any;
}

export function QuantityDistributionDisplay({ 
  specifications, 
  deliveryLocations,
  matrix,
}: QuantityDistributionDisplayProps) {
  // Check if we have any data with quantities (not percentages)
  const hasQuantities = Object.values(matrix).some((specMatrix: any) => 
    Object.values(specMatrix).some((item: any) => item.quantity != null && item.quantity > 0)
  );

  const hasPercentages = Object.values(matrix).some((specMatrix: any) => 
    Object.values(specMatrix).some((item: any) => item.percentage != null && item.percentage > 0)
  );

  const getQuantity = (specId: string, locationId: string): number => {
    return matrix[specId]?.[locationId]?.quantity || 0;
  };

  const getPercentage = (specId: string, locationId: string): number => {
    return matrix[specId]?.[locationId]?.percentage || 0;
  };

  const getSpecificationTotal = (specId: string): number => {
    if (!matrix[specId]) return 0;
    if (hasQuantities) {
      return Object.values(matrix[specId] as Record<string, any>).reduce(
        (sum: number, item: any) => sum + (Number(item.quantity) || 0), 
        0
      );
    }
    return Object.values(matrix[specId] as Record<string, any>).reduce(
      (sum: number, item: any) => sum + (Number(item.percentage) || 0), 
      0
    );
  };

  if (!hasQuantities && !hasPercentages) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Quantity Distribution by Location
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No quantity distribution defined for this RFQ.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Quantity Distribution by Location
        </CardTitle>
        <CardDescription>
          {hasQuantities 
            ? "Quantities allocated to each delivery location for this order."
            : "Percentage distribution to each delivery location. Final quantities will be calculated based on the awarded quantity option."
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">Specification</TableHead>
                {deliveryLocations.map(location => (
                  <TableHead key={location.id} className="text-center min-w-[140px]">
                    <div className="font-semibold">{location.delivery_name}</div>
                    <div className="text-xs text-muted-foreground font-normal">
                      {location.city}, {location.state_province}
                    </div>
                  </TableHead>
                ))}
                <TableHead className="text-center min-w-[100px]">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {specifications.map(spec => {
                const total = getSpecificationTotal(spec.id);
                
                return (
                  <TableRow key={spec.id}>
                    <TableCell>
                      <div className="font-medium">{spec.specification_name}</div>
                      <Badge variant="outline" className="mt-1 text-xs capitalize">
                        {spec.specification_type?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    {deliveryLocations.map(location => (
                      <TableCell key={location.id} className="text-center">
                        {hasQuantities ? (
                          <span className="font-medium">
                            {getQuantity(spec.id, location.id).toLocaleString()}
                          </span>
                        ) : (
                          <span className="font-medium">
                            {getPercentage(spec.id, location.id).toFixed(1)}%
                          </span>
                        )}
                      </TableCell>
                    ))}
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="font-semibold">
                        {hasQuantities 
                          ? total.toLocaleString()
                          : `${total.toFixed(1)}%`
                        }
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}